/**
 * ProjectDetail — header + two-column body: a tabbed main area
 * (Description · Tasks [List/Kanban] · Files) and a narrow People/Details rail.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "../../utils/toast";
import { ArrowLeft, Plus, Check, MessageCircle, Clock, FileText, ListChecks, FolderOpen, List, Columns, Trash2 } from "lucide-react";
import { wsApi, wsBase, isManager, assetUrl } from "./wsApi";
import { hasPermission } from "../../utils/permissionUtils";
import {
  TASK_STATUS, TASK_STATUS_ORDER, PRIORITY, PROJECT_STATUS, fmtDate, fmtDuration,
} from "./wsConstants";
import { T, Avatar, PrimaryButton, StatusBadge, Tabs, ConfirmDialog } from "../../components/em/ui";
import { ColorBadge, money, Pills, Skeleton } from "./wsUi";
import TaskModal from "./TaskModal";
import DescriptionTab from "./project/DescriptionTab";
import FilesTab from "./project/FilesTab";
import ProjectSidebar from "./project/ProjectSidebar";

const PriorityTag = ({ priority }) => {
  const p = PRIORITY[priority] || PRIORITY.medium;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 6, background: p.bg, color: p.text, font: `600 10px ${T.font}`, textTransform: "uppercase", letterSpacing: "0.03em", whiteSpace: "nowrap" }}>
      {p.label}
    </span>
  );
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // `manager` used to mean both "can edit this project" AND "can see its
  // margin". Those are two different permissions, and a project manager should
  // get the first without the second — so they are split here.
  const manager = isManager() || hasPermission("manage_projects");
  const canSeeProfit = isManager() || hasPermission("view_workspace_analytics");
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [profit, setProfit] = useState(null);
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState("tasks");
  const [taskView, setTaskView] = useState("list");
  const [filesKey, setFilesKey] = useState(0); // bump to refresh Files tab
  const [confirmTask, setConfirmTask] = useState(null); // task awaiting delete
  const [deletingTask, setDeletingTask] = useState(false);

  const loadProject = useCallback(() => {
    wsApi.getProject(id).then(setProject).catch(() => toast.error("Could not load project"));
  }, [id]);
  const loadTasks = useCallback(() => {
    wsApi.projectTasks(id).then(setTasks).catch(() => {});
  }, [id]);

  useEffect(() => {
    setLoading(true);
    wsApi.getProject(id).then(setProject)
      .catch(() => toast.error("Could not load project"))
      .finally(() => setLoading(false));
    loadTasks();
    wsApi.users().then(setUsers).catch(() => {});
    if (canSeeProfit) {
      wsApi.projectProfitability(id).then(setProfit).catch(() => {});
      wsApi.projectBudget(id).then(setBudget).catch(() => {});
    }
  }, [id, loadTasks, canSeeProfit]);

  const refreshProfit = useCallback(() => {
    if (canSeeProfit) {
      wsApi.projectProfitability(id).then(setProfit).catch(() => {});
      wsApi.projectBudget(id).then(setBudget).catch(() => {});
    }
  }, [id, canSeeProfit]);

  // Time logged inside the open TaskModal (or a live timer stopping in the
  // header) has to refetch here — those paths can't use onSaved, which closes
  // the modal. Both fire `workspaceDataChanged`. loadProject() is part of this
  // now because "Hours logged" reads project.logged_minutes, not tasks[].
  useEffect(() => {
    const handler = () => { loadProject(); loadTasks(); refreshProfit(); };
    window.addEventListener("workspaceDataChanged", handler);
    return () => window.removeEventListener("workspaceDataChanged", handler);
  }, [loadProject, loadTasks, refreshProfit]);

  const moveTask = async (task, status) => {
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status } : t)));
    try { await wsApi.updateTaskStatus(task.id, status); refreshProfit(); }
    catch { toast.error("Could not update task"); loadTasks(); }
  };

  // Deleting takes the task's comments and logged time with it, so it asks
  // first, and the row goes only once the server has confirmed.
  const removeTask = async () => {
    if (!confirmTask) return;
    setDeletingTask(true);
    try {
      await wsApi.deleteTask(confirmTask.id);
      setTasks((prev) => prev.filter((t) => t.id !== confirmTask.id));
      refreshProfit();
      toast.success("Task deleted");
      setConfirmTask(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not delete task");
    } finally {
      setDeletingTask(false);
    }
  };

  if (loading) return (
    <div>
      <Skeleton h={150} radius={14} style={{ marginBottom: 16 }} />
      <div style={{ display: "flex", gap: 16 }}>
        <Skeleton h={360} radius={14} style={{ flex: 1 }} />
        <Skeleton w={300} h={280} radius={14} />
      </div>
    </div>
  );
  if (!project) return <div style={{ padding: 40 }}>Project not found.</div>;

  const done = tasks.filter((t) => t.status === "done").length;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;
  // Project-wide total from the server, which counts time logged straight
  // against the project (no task) as well as task time. Summing tasks[] here
  // used to miss every Timesheets entry, since those carry no task_id — a
  // project could show "0m" while the burn-down below reported 13.4h.
  // Falls back to the task sum only if an older payload omits the field.
  const loggedMins =
    project?.logged_minutes != null
      ? Number(project.logged_minutes) || 0
      : tasks.reduce((s, t) => s + (Number(t.logged_minutes) || 0), 0);

  const TABS = [
    { key: "description", label: "Description", Icon: FileText },
    { key: "tasks", label: "Tasks", Icon: ListChecks },
    { key: "files", label: "Files", Icon: FolderOpen },
  ];

  return (
    <div>
      <button onClick={() => navigate(`${wsBase()}/projects`)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: 0, color: T.fg5, font: `500 12px ${T.font}`, cursor: "pointer", marginBottom: 14, padding: 0 }}>
        <ArrowLeft size={14} /> All Projects
      </button>

      {/* Header card */}
      <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 16, padding: 22, boxShadow: T.shadowSm, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          {project.logo ? (
            <div style={{ width: 52, height: 52, borderRadius: 12, overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}` }}>
              <img src={assetUrl(project.logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
          ) : (
            <ColorBadge color={project.color || T.orange} size={52} label={(project.name || "").slice(0, 1).toUpperCase()} />
          )}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h2 style={{ margin: 0, font: `700 22px ${T.font}`, letterSpacing: "-0.02em", color: T.fg1 }}>{project.name}</h2>
              <StatusBadge label={(PROJECT_STATUS[project.status] || PROJECT_STATUS.planning).label} />
            </div>
            <div style={{ font: `400 12px ${T.font}`, color: T.fg5, marginTop: 4 }}>{project.client_name || "No client"}</div>
          </div>
          {tab === "tasks" && (
            <PrimaryButton icon={Plus} onClick={() => setModal({ create: true })}>New Task</PrimaryButton>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginTop: 20, paddingTop: 18, borderTop: `1px solid ${T.divider}` }}>
          {[
            { l: "Progress", v: `${pct}%` },
            { l: "Tasks done", v: `${done}/${tasks.length}` },
            { l: "Hours logged", v: fmtDuration(loggedMins) },
            { l: canSeeProfit && profit ? "Margin" : "Deadline", v: canSeeProfit && profit ? `${profit.margin}%` : (project.deadline ? fmtDate(project.deadline) : "—"), c: canSeeProfit && profit ? (profit.profit >= 0 ? T.success600 : T.danger) : undefined },
          ].map((s) => (
            <div key={s.l}>
              <div style={{ font: `700 18px ${T.font}`, color: s.c || T.fg1, fontVariantNumeric: "tabular-nums" }}>{s.v}</div>
              <div style={{ font: `500 11px ${T.font}`, color: T.fg7, marginTop: 3 }}>{s.l}</div>
            </div>
          ))}
        </div>
        {canSeeProfit && profit && (
          <div style={{ display: "flex", gap: 18, marginTop: 14, font: `500 12px ${T.font}` }}>
            <span style={{ color: T.success600 }}>Revenue {money(profit.revenue)}</span>
            <span style={{ color: T.danger }}>Cost {money(profit.cost)}</span>
            <span style={{ color: profit.profit >= 0 ? T.success600 : T.danger, fontWeight: 700 }}>Profit {money(profit.profit)}</span>
          </div>
        )}
        {canSeeProfit && budget && budget.budget > 0 && (() => {
          const over = budget.pct != null && budget.pct > 100;
          const barPct = Math.min(100, budget.pct || 0);
          const barColor = over ? T.danger : budget.pct > 80 ? "#f59e0b" : T.success600;
          return (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.divider}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <span style={{ font: `600 12px ${T.font}`, color: T.fg2 }}>
                  Budget burn-down{budget.budget_type ? ` · ${budget.budget_type}` : ""}
                </span>
                <span style={{ font: `600 12px ${T.font}`, color: barColor }}>
                  {money(budget.spent_cost)} / {money(budget.budget)} ({budget.pct}%)
                </span>
              </div>
              <div style={{ height: 10, borderRadius: 9999, background: T.bgMuted, overflow: "hidden" }}>
                <div style={{ width: `${barPct}%`, height: "100%", background: barColor, transition: "width .3s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, font: `500 11px ${T.font}`, color: T.fg7 }}>
                <span>{budget.logged_hours}h logged</span>
                <span style={{ color: over ? T.danger : T.fg7, fontWeight: over ? 700 : 500 }}>
                  {over ? `Over budget by ${money(Math.abs(budget.remaining))}` : `${money(budget.remaining)} remaining`}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Two-column body */}
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Tab bar — shared segmented control (same as Attendance/Leaves) */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
            <Tabs
              tabs={TABS.map((tb) => ({ key: tb.key, label: tb.label, icon: tb.Icon }))}
              active={tab} onChange={setTab}
            />
            {tab === "tasks" && (
              <div style={{ marginLeft: "auto" }}>
                <Pills
                  options={[{ value: "list", label: "List", icon: List }, { value: "kanban", label: "Kanban", icon: Columns }]}
                  value={taskView} onChange={setTaskView}
                />
              </div>
            )}
          </div>

          {tab === "description" && (
            <DescriptionTab projectId={id} initialDoc={project.description_doc} onFilesChanged={() => setFilesKey((k) => k + 1)} />
          )}
          {tab === "files" && <FilesTab key={filesKey} projectId={id} canManage={manager} />}
          {tab === "tasks" && (
            <TasksPanel
              tasks={tasks} view={taskView} onOpen={(taskId) => setModal({ taskId })}
              onToggleDone={(t) => moveTask(t, t.status === "done" ? "todo" : "done")}
              onCreate={() => setModal({ create: true })}
              onMove={moveTask}
              // HR is admin-tier — DELETE /tasks/:id accepts it server-side.
              onDelete={manager ? setConfirmTask : null}
            />
          )}
        </div>

        <ProjectSidebar project={project} users={users} canManage={manager} onChanged={loadProject} />
      </div>

      {modal && (
        <TaskModal
          taskId={modal.taskId}
          presetProjectId={id}
          projects={[project]}
          users={users}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadTasks(); refreshProfit(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmTask}
        title="Delete task?"
        message={`"${confirmTask?.title}" will be removed, along with its comments and logged time.`}
        confirmLabel="Delete task"
        danger
        busy={deletingTask}
        onConfirm={removeTask}
        onCancel={() => !deletingTask && setConfirmTask(null)}
      />
    </div>
  );
}

// index.css skins every bare <button> with height:40px / line-height:40px, so
// an icon button must state its own box — otherwise the glyph renders at the
// top of a 40px block and floats above the avatars it sits beside.
const rowIconBtn = {
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  padding: 0,
  margin: 0,
  lineHeight: 1,
  border: 0,
  borderRadius: 8,
  background: "none",
  color: T.fg7,
  cursor: "pointer",
};

// ── Tasks panel (List + Kanban) ───────────────────────────────────────────────
function TasksPanel({ tasks, view, onOpen, onToggleDone, onCreate, onMove, onDelete }) {
  if (tasks.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "50px 20px", color: T.fg7, background: "var(--em-bg-card)", border: `1px dashed ${T.border}`, borderRadius: 14 }}>
        <div style={{ font: `600 15px ${T.font}`, color: T.fg1, marginBottom: 6 }}>No tasks yet</div>
        <div style={{ font: `400 13px ${T.font}`, marginBottom: 14 }}>Add the first task to get this project moving.</div>
        <div style={{ display: "inline-flex" }}><PrimaryButton icon={Plus} onClick={onCreate}>New Task</PrimaryButton></div>
      </div>
    );
  }

  const TaskRow = ({ t, i }) => {
    const assignees = (t.members || []).filter((m) => m.member_role === "assignee");
    const isDone = t.status === "done";
    return (
      <div onClick={() => onOpen(t.id)}
        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: i ? `1px solid ${T.divider}` : "none", cursor: "pointer" }}
        onMouseEnter={(e) => (e.currentTarget.style.background = T.bgHead)}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
        <button onClick={(e) => { e.stopPropagation(); onToggleDone(t); }}
          style={{ width: 18, height: 18, borderRadius: 9999, border: `2px solid ${isDone ? T.success600 : "#cbd5e1"}`, background: isDone ? T.success600 : "transparent", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          {isDone && <Check size={11} color="#fff" />}
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `500 13px ${T.font}`, color: T.fg1, textDecoration: isDone ? "line-through" : "none", opacity: isDone ? 0.6 : 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.title}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, font: `400 11px ${T.font}`, color: T.fg7 }}>
            {t.comment_count > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><MessageCircle size={11} />{t.comment_count}</span>}
            {t.logged_minutes > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><Clock size={11} />{fmtDuration(t.logged_minutes)}</span>}
          </div>
        </div>
        <PriorityTag priority={t.priority} />
        {t.due_date && <span style={{ font: `500 11px ${T.font}`, color: T.fg5, width: 64, textAlign: "right" }}>{fmtDate(t.due_date)}</span>}
        <div style={{ display: "flex" }}>
          {assignees.slice(0, 3).map((m, idx) => (
            <span key={m.user_id} style={{ marginLeft: idx ? -7 : 0, border: "2px solid var(--em-bg-card)", borderRadius: 9999 }}>
              <Avatar name={m.username} size={24} />
            </span>
          ))}
        </div>
        {onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(t); }}
            title="Delete task" aria-label={`Delete ${t.title}`}
            style={rowIconBtn}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
    );
  };

  if (view === "kanban") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${TASK_STATUS_ORDER.length}, minmax(0,1fr))`, gap: 12, alignItems: "start" }}>
        {TASK_STATUS_ORDER.map((s) => {
          const col = TASK_STATUS[s];
          const list = tasks.filter((t) => t.status === s);
          return (
            <div key={s} style={{ background: "var(--em-bg-head)", border: `1px solid ${T.border}`, borderRadius: 12, padding: 10, minHeight: 120 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "0 4px" }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: col.dot }} />
                <span style={{ font: `700 11px ${T.font}`, color: T.fg1, textTransform: "uppercase", letterSpacing: "0.04em", flex: 1 }}>{col.label}</span>
                <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>{list.length}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {list.map((t) => (
                  <div key={t.id} onClick={() => onOpen(t.id)}
                    style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 9, padding: "10px 11px", cursor: "pointer", boxShadow: T.shadowSm }}>
                    <div style={{ font: `500 12.5px ${T.font}`, color: T.fg1, marginBottom: 6 }}>{t.title}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <PriorityTag priority={t.priority} />
                      <div style={{ flex: 1 }} />
                      {/* Quick-advance to the next column */}
                      {TASK_STATUS_ORDER.indexOf(s) < TASK_STATUS_ORDER.length - 1 && (
                        <button onClick={(e) => { e.stopPropagation(); onMove(t, TASK_STATUS_ORDER[TASK_STATUS_ORDER.indexOf(s) + 1]); }}
                          title="Move to next" style={{ background: "none", border: 0, cursor: "pointer", color: T.fg5, font: `600 11px ${T.font}` }}>→</button>
                      )}
                      {onDelete && (
                        <button onClick={(e) => { e.stopPropagation(); onDelete(t); }}
                          title="Delete task" aria-label={`Delete ${t.title}`}
                          style={{ ...rowIconBtn, width: 22, height: 22 }}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {!list.length && <div style={{ padding: "12px 4px", color: T.fg7, font: `400 11px ${T.font}`, textAlign: "center" }}>—</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // List view — grouped by status
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {TASK_STATUS_ORDER.map((s) => {
        const col = TASK_STATUS[s];
        const list = tasks.filter((t) => t.status === s);
        if (!list.length) return null;
        return (
          <div key={s}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: 9999, background: col.dot }} />
              <span style={{ font: `700 12px ${T.font}`, color: T.fg1, textTransform: "uppercase", letterSpacing: "0.04em" }}>{col.label}</span>
              <span style={{ font: `600 11px ${T.font}`, color: T.fg7 }}>{list.length}</span>
            </div>
            <div style={{ background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
              {list.map((t, i) => <TaskRow key={t.id} t={t} i={i} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
